import React, { useMemo, useState } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";

import { didRequestReachServer } from "@api/apiError";
import { useUpdateBookkeepingStartDateMutation } from "@api/entity.api";
import { getEarliestSelfServeBookkeepingStartDate } from "@common/helpers/bookkeepingStartDate";
import {
    fullDateFullYearFormat,
    fullDateLongFormat,
} from "@common/helpers/date";
import { useEntity } from "@components/entity/useEntity";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { VStack } from "@design-system/VStack/VStack";
import { formatDateOnly } from "@helpers/date";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { EditModalFooter } from "./EditModalFooter";

const updateBookkeepingStartDateFormSchema = z.object({
    bookkeepingStartDate: z.string().date("Select a date"),
});

type UpdateBookkeepingStartDateFormValues = z.infer<
    typeof updateBookkeepingStartDateFormSchema
>;

const fields = createFormikFields<UpdateBookkeepingStartDateFormValues>();

interface Props {
    show: boolean;
    onClose(): void;
}

export const UpdateBookkeepingStartDateModal: React.FC<Props> = ({
    show,
    onClose,
}) => {
    const entity = useEntity();
    const workspace = useActiveWorkspace();
    const updateStartDate = useUpdateBookkeepingStartDateMutation(entity);
    const { error } = useToaster();
    const [pendingDate, setPendingDate] = useState<string | null>(null);
    // oxlint-disable-next-line react/hook-use-state -- The earliest allowed date is based on the time the modal opened.
    const [now] = useState(() => new Date());

    const minDate = useMemo(() => {
        if (!workspace) {
            return undefined;
        }
        const earliestAllowedDate = getEarliestSelfServeBookkeepingStartDate({
            now,
            timezone: workspace.timezone,
        });
        return entity.bookkeepingStartDate < earliestAllowedDate
            ? entity.bookkeepingStartDate
            : earliestAllowedDate;
    }, [workspace, entity.bookkeepingStartDate, now]);

    const formik = useFormik<UpdateBookkeepingStartDateFormValues>({
        initialValues: {
            bookkeepingStartDate: entity.bookkeepingStartDate,
        },
        validate: validateFormikWithZodSchema(
            updateBookkeepingStartDateFormSchema,
        ),
        onSubmit: submitHelper({
            handler: async ({ bookkeepingStartDate }) => {
                if (bookkeepingStartDate === entity.bookkeepingStartDate) {
                    onClose();
                    return;
                }
                setPendingDate(bookkeepingStartDate);
            },
        }),
    });

    const confirmChange = async () => {
        if (!pendingDate) {
            return;
        }
        try {
            await updateStartDate.mutateAsync(pendingDate);
            setPendingDate(null);
            onClose();
        } catch (requestError) {
            if (!didRequestReachServer(requestError)) {
                error("Failed to update bookkeeping start date");
            }
            setPendingDate(null);
        }
    };

    const currentDateLabel = formatDateOnly(
        entity.bookkeepingStartDate,
        fullDateFullYearFormat,
    );
    const pendingDateLabel = formatDateOnly(
        pendingDate,
        fullDateFullYearFormat,
    );

    return (
        <>
            <PlainModal show={show && pendingDate === null} onHide={onClose}>
                <StandardModalHeader>
                    Update bookkeeping start date
                </StandardModalHeader>
                <FormikProvider value={formik}>
                    <Form>
                        <VStack gap={16}>
                            <fields.Date
                                name="bookkeepingStartDate"
                                label="Bookkeeping start date"
                                pickerLabel="Bookkeeping start date"
                                variant="panel-input"
                                displayFormat={fullDateLongFormat}
                                minDate={minDate}
                            />
                            <EditModalFooter
                                onClose={onClose}
                                isValid={formik.isValid}
                                isSubmitting={formik.isSubmitting}
                            />
                        </VStack>
                    </Form>
                </FormikProvider>
            </PlainModal>
            <ConfirmationModal
                show={show && pendingDate !== null}
                title="Update bookkeeping start date?"
                question={
                    <VStack gap={8}>
                        <span>
                            {currentDateLabel} → {pendingDateLabel}
                        </span>
                        <span>
                            Transactions before {pendingDateLabel} are excluded
                            from your books. Journal entries regenerate, which
                            can take a few minutes.
                        </span>
                    </VStack>
                }
                yes="Update"
                no="Cancel"
                onReject={() => setPendingDate(null)}
                onHide={() => setPendingDate(null)}
                onConfirm={confirmChange}
            />
        </>
    );
};
